import { useEffect, useState } from "react";
import apiClient from "../../../../services/api";
import { SCOPING_STAGES } from "../../../../constants";
import { getInit, relTime } from "../../../../utils";
import {
  Button,
  Card,
  Chip,
  FieldGroup,
  FieldLabel,
  Input,
  Panel,
  PanelRow,
  Select,
} from "../../../../shared/ui";
import { cn } from "../../../../lib/cn";

const SCOPING_STAGE_LABEL_TO_ENUM = {
  "1. New": "NEW",
  "2. Customer Info": "CUSTOMER_INFO",
  "3. Estimation": "ESTIMATION",
  "4. Proposal": "PROPOSAL",
  "5. Developer Matching": "DEVELOPER_MATCHING",
  "6. Meeting": "MEETING",
  "7. Done": "DONE",
  "8. Lost": "LOST",
};

const SCOPING_STAGE_ENUM_TO_LABEL = Object.fromEntries(
  Object.entries(SCOPING_STAGE_LABEL_TO_ENUM).map(([label, value]) => [value, label])
);

// codex.md RBAC roles land first; legacy values stay selectable so existing
// users can be reassigned without surprise.
const ROLE_OPTIONS = [
  "SUPER_ADMIN", "CEO", "CFO", "COO", "CCO", "PM", "HR",
  "ADMIN", "MANAGER", "CONSULTANT", "CLIENT_VIEW",
];
const ROLE_LABEL = {
  SUPER_ADMIN: "Super Admin",
  CEO: "CEO", CFO: "CFO", COO: "COO", CCO: "CCO", PM: "PM", HR: "HR",
  ADMIN: "Admin (legacy)",
  MANAGER: "Manager (legacy)",
  CONSULTANT: "Consultant (legacy)",
  CLIENT_VIEW: "Client View (legacy)",
};

const STATUS_TONE = {
  ACTIVE:         { tone: "success", label: "Active" },
  PENDING_INVITE: { tone: "warn",    label: "Pending" },
  INACTIVE:       { tone: "muted",   label: "Inactive" },
};

function AvatarBadge({ initials, size = 24 }) {
  return (
    <div className="avatar" style={{ width: size, height: size, fontSize: size * 0.34 }}>
      {initials}
    </div>
  );
}

// Phase 5f — TeamPage migrated to Tailwind + shared primitives. Replaces
// the tp-* / team-* class families from profile.css (those rules are
// deleted in this commit).
export default function TeamPage({ currentUser, teams, setTeams }) {
  const [users, setUsers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("CONSULTANT");
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [teamForm, setTeamForm] = useState({ name: "", stage: SCOPING_STAGES[0], memberIds: [] });

  useEffect(() => {
    apiClient.getUsers()
      .then((data) => {
        setUsers(data.users || data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  async function handleInvite() {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setMsg(null);
    try {
      const result = await apiClient.inviteUser(inviteEmail.trim(), inviteRole);
      setUsers((current) => [...(result.user ? [result.user] : []), ...current]);
      setInviteEmail("");
      setMsg({ type: "ok", text: `Invitation sent to ${inviteEmail}` });
    } catch (error) {
      setMsg({ type: "err", text: error?.response?.data?.error || "Failed to invite user" });
    } finally {
      setInviting(false);
    }
  }

  function toggleTeamMember(userId) {
    setTeamForm((current) => ({
      ...current,
      memberIds: current.memberIds.includes(userId)
        ? current.memberIds.filter((id) => id !== userId)
        : [...current.memberIds, userId],
    }));
  }

  async function createTeam() {
    if (!teamForm.name.trim()) return;
    try {
      const stageValue = SCOPING_STAGE_LABEL_TO_ENUM[teamForm.stage] || teamForm.stage;
      const result = await apiClient.createTeam({
        name: teamForm.name.trim(),
        stage: stageValue,
        memberIds: teamForm.memberIds,
      });
      const newTeam = result.team;
      if (newTeam) {
        setTeams((current) => [
          {
            ...newTeam,
            stageLabel: SCOPING_STAGE_ENUM_TO_LABEL[newTeam.stage] || newTeam.stage,
            members: newTeam.members || [],
          },
          ...current,
        ]);
      }
      setTeamForm({ name: "", stage: SCOPING_STAGES[0], memberIds: [] });
      setMsg({ type: "ok", text: `Team "${teamForm.name.trim()}" created` });
    } catch (error) {
      console.error("Failed to create team", error);
      setMsg({ type: "err", text: "Failed to create team" });
    }
  }

  async function handleRoleChange(userId, role) {
    try {
      await apiClient.updateUserRole(userId, role);
      setUsers((current) => current.map((user) => (user.id === userId ? { ...user, role } : user)));
    } catch {
      setMsg({ type: "err", text: "Failed to update role" });
    }
  }

  async function handleStatusToggle(user) {
    const newStatus = user.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await apiClient.updateUserStatus(user.id, newStatus);
      setUsers((current) => current.map((item) => (item.id === user.id ? { ...item, status: newStatus } : item)));
    } catch {
      setMsg({ type: "err", text: "Failed to update status" });
    }
  }

  const isSuperAdmin = currentUser?.role === "SUPER_ADMIN";

  return (
    <div className="px-9 pt-7 pb-14 max-w-[1100px]">
      <div className="mb-7">
        <div className="pg-title">Team &amp; Access</div>
        <div className="pg-sub">Manage scoping teams, invite members, and control platform roles.</div>
      </div>

      {msg && (
        <div className={cn(
          "px-4 py-2.5 rounded-lg mb-5 text-[13px] font-semibold border",
          msg.type === "ok"
            ? "bg-[rgba(16,185,129,0.1)] text-[#059669] border-[rgba(16,185,129,0.2)]"
            : "bg-[rgba(239,68,68,0.08)] text-[#dc2626] border-[rgba(239,68,68,0.2)]",
        )}>
          {msg.text}
        </div>
      )}

      {/* ── Scoping Teams ─────────────────────────────────────────── */}
      <section className="mb-9">
        <header className="mb-4">
          <div className="text-[15px] font-bold text-rc-text-primary mb-1">Scoping Teams</div>
          <div className="text-xs text-[#4a6a96]">Each team is assigned to a pipeline stage and auto-populated on card moves.</div>
        </header>

        <div className="grid gap-5 items-start" style={{ gridTemplateColumns: "300px 1fr" }}>
          {/* New team form */}
          <div className="bg-rc-surface border border-rc-border rounded-xl p-5 flex flex-col gap-3.5">
            <div className="text-[13px] font-bold text-rc-text-primary mb-0.5">New Team</div>
            <FieldGroup>
              <FieldLabel>Team name</FieldLabel>
              <Input
                placeholder="e.g. Developer Matching Team"
                value={teamForm.name}
                onChange={(event) => setTeamForm((current) => ({ ...current, name: event.target.value }))}
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Assigned stage</FieldLabel>
              <Select
                value={teamForm.stage}
                onChange={(event) => setTeamForm((current) => ({ ...current, stage: event.target.value }))}
              >
                {SCOPING_STAGES.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
              </Select>
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Members</FieldLabel>
              <div className="flex flex-col gap-1 max-h-40 overflow-y-auto">
                {users.map((user) => {
                  const selected = teamForm.memberIds.includes(user.id);
                  return (
                    <label
                      key={user.id}
                      className={cn(
                        "flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors",
                        "text-xs border border-transparent",
                        selected
                          ? "bg-[rgba(59,127,250,0.1)] border-[rgba(59,127,250,0.24)] text-rc-text-primary"
                          : "text-rc-text-secondary hover:bg-rc-surface-hover",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleTeamMember(user.id)}
                        className="hidden"
                      />
                      <AvatarBadge initials={user.initials || getInit(user.name)} size={24} />
                      <span>{user.name}</span>
                      {selected && <span className="ml-auto text-[11px] font-bold text-rc-accent">✓</span>}
                    </label>
                  );
                })}
              </div>
            </FieldGroup>
            <Button onClick={createTeam} disabled={!teamForm.name.trim()}>
              Create team
            </Button>
          </div>

          {/* Teams list */}
          <div className="flex flex-col gap-2.5">
            {teams.length === 0 && (
              <div className="bg-rc-surface border border-rc-border rounded-[10px] py-6 text-center text-[13px] text-[#4a6a96]">
                No teams yet. Create your first team.
              </div>
            )}
            {teams.map((team) => {
              const memberNames = (team.members || []).map((member) => member.user?.name || member.name).filter(Boolean);
              return (
                <Card key={team.id} className="px-[18px] py-4 hover:border-[rgba(59,127,250,0.3)] transition-colors">
                  <div className="flex items-start justify-between mb-2.5">
                    <div>
                      <div className="text-sm font-bold text-rc-text-primary">{team.name}</div>
                      <div className="text-[11px] text-[#4a6a96] mt-0.5">{team.stageLabel || team.stage}</div>
                    </div>
                    <span className="text-[11px] text-rc-text-secondary bg-rc-surface-hover border border-rc-border rounded-full px-2.5 py-0.5 whitespace-nowrap">
                      {memberNames.length} member{memberNames.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {memberNames.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {memberNames.map((name, index) => (
                        <div key={index} className="flex items-center gap-1.5 bg-rc-surface-hover border border-rc-border rounded-full px-2.5 py-0.5 pl-1 text-[11px] text-rc-text-secondary">
                          <AvatarBadge initials={getInit(name)} size={20} />
                          <span>{name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Invite Member ─────────────────────────────────────────── */}
      <section className="mb-9">
        <header className="mb-4">
          <div className="text-[15px] font-bold text-rc-text-primary mb-1">Invite Member</div>
          <div className="text-xs text-[#4a6a96]">Send an invite link to a new team member. They must sign in with their Raincode Google account.</div>
        </header>
        <div className="flex gap-3 items-end bg-rc-surface border border-rc-border rounded-xl px-5 py-[18px] flex-wrap">
          <FieldGroup className="flex-1">
            <FieldLabel>Email address</FieldLabel>
            <Input
              type="email"
              placeholder="colleague@raincode.tech"
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && handleInvite()}
            />
          </FieldGroup>
          <FieldGroup className="w-40 shrink-0">
            <FieldLabel>Role</FieldLabel>
            <Select value={inviteRole} onChange={(event) => setInviteRole(event.target.value)}>
              {ROLE_OPTIONS.map((role) => <option key={role} value={role}>{ROLE_LABEL[role]}</option>)}
            </Select>
          </FieldGroup>
          <Button onClick={handleInvite} disabled={inviting}>
            {inviting ? "Sending…" : "Send invite"}
          </Button>
        </div>
      </section>

      {/* ── Platform Users ────────────────────────────────────────── */}
      <section className="mb-9">
        <header className="mb-4">
          <div className="text-[15px] font-bold text-rc-text-primary mb-1">Platform Users</div>
          <div className="text-xs text-[#4a6a96]">{users.length} member{users.length !== 1 ? "s" : ""} · manage roles and account status.</div>
        </header>
        {loading ? (
          <div className="py-10 text-center text-rc-text-dim">Loading users…</div>
        ) : (
          <Panel>
            {users.map((user) => {
              const statusKey = user.status || "INACTIVE";
              const tone = STATUS_TONE[statusKey]?.tone || "muted";
              const label = STATUS_TONE[statusKey]?.label || statusKey || "—";
              const isSelf = user.id === currentUser?.id;
              return (
                <PanelRow key={user.id} className="gap-3.5 px-5 py-3.5">
                  <AvatarBadge initials={user.initials || getInit(user.name)} size={38} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-bold text-rc-text-primary">{user.name}</div>
                    <div className="text-[11.5px] text-rc-text-secondary mt-px">{user.email}</div>
                    <div className="text-[10.5px] text-[#4a6a96] mt-0.5">
                      Last login: {user.lastLoginAt ? relTime(user.lastLoginAt) : "Never"}
                      {user.invitedAt && !user.inviteAcceptedAt && " · Invite pending"}
                    </div>
                  </div>
                  <Chip size="sm" tone={tone}>{label}</Chip>
                  {isSuperAdmin && !isSelf ? (
                    <Select
                      value={user.role}
                      onChange={(event) => handleRoleChange(user.id, event.target.value)}
                      className="!py-1 !px-2 !text-xs !w-auto"
                    >
                      {ROLE_OPTIONS.map((role) => <option key={role} value={role}>{ROLE_LABEL[role]}</option>)}
                    </Select>
                  ) : (
                    <span className="text-xs text-rc-text-secondary min-w-[100px]">
                      {ROLE_LABEL[user.role] || user.role}
                    </span>
                  )}
                  {!isSelf && (
                    <Button
                      size="xs"
                      variant={user.status === "ACTIVE" ? "destructive" : "secondary"}
                      onClick={() => handleStatusToggle(user)}
                    >
                      {user.status === "ACTIVE" ? "Deactivate" : "Reactivate"}
                    </Button>
                  )}
                </PanelRow>
              );
            })}
          </Panel>
        )}
      </section>
    </div>
  );
}
